<script lang="ts">
  import { tick } from "svelte";
  import { observe } from "$lib/actions/observe";
  import { POST } from "$lib/api";
  import { isValidEmail } from "$lib/validation/email";

  let { sectionLabel = "", headline = "", email = "office@practical.at", phone = "+43 1 600 73 50", linkedin = "#", bookingUrl = "#" } = $props();

  let name = $state("");
  let emailField = $state("");
  let phoneField = $state("");
  let role = $state("");
  let website = $state("");
  let subject = $state("");
  let message = $state("");
  let submitted = $state(false);
  let submitting = $state(false);
  let submitError = $state(false);
  let company = $state(""); // honeypot — must stay empty
  let errors = $state<Record<string, boolean>>({});

  let successEl = $state<HTMLElement>();
  let errorEl = $state<HTMLElement>();

  function scrollToMessage(el: HTMLElement | undefined) {
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  const messages: Record<string, { de: string; en: string }> = {
    name: { de: "Bitte geben Sie Ihren Namen ein", en: "Please enter your name" },
    email: { de: "Bitte geben Sie eine gültige E-Mail-Adresse ein", en: "Please enter a valid email address" },
    message: { de: "Bitte geben Sie Ihre Nachricht ein", en: "Please enter your message" }
  };

  function validate() {
    const e: Record<string, boolean> = {};
    if (!name.trim()) e.name = true;
    if (!isValidEmail(emailField)) e.email = true;
    if (!message.trim()) e.message = true;
    errors = e;
    return Object.keys(e).length === 0;
  }

  async function handleSubmit() {
    submitError = false;
    if (!validate()) return;
    submitting = true;
    try {
      const res = await POST("contact", { name, email: emailField, phone: phoneField, role, website, subject, message, company });
      if (res?.ok) {
        submitted = true;
        name = "";
        emailField = "";
        phoneField = "";
        role = "";
        website = "";
        subject = "";
        message = "";
        await tick();
        scrollToMessage(successEl);
      } else {
        submitError = true;
        await tick();
        scrollToMessage(errorEl);
      }
    } catch (err) {
      console.error(err);
      submitError = true;
      await tick();
      scrollToMessage(errorEl);
    } finally {
      submitting = false;
    }
  }
</script>

<section>
  <div class="container">
    {#if sectionLabel}
      <div class="section-divider fade-up" use:observe>
        <span class="section-label">{sectionLabel}</span>
        <h2>{headline}</h2>
      </div>
    {/if}
    <div class="contact-grid fade-up" use:observe>
      <div>
        {#if submitted}
          <div class="card" role="status" bind:this={successEl} style="background:var(--success-bg);border-color:var(--success)">
            <p lang="de" style="color:var(--success);font-weight:600">Vielen Dank für Ihre Nachricht! Wir melden uns innerhalb von 24 Stunden.</p>
            <p lang="en" style="color:var(--success);font-weight:600">Thank you for your message! We'll get back to you within 24 hours.</p>
          </div>
        {:else}
          <form
            onsubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            novalidate
          >
            <div class="form-group">
              <label lang="de" for="name">Name *</label>
              <label lang="en" for="name">Name *</label>
              <input id="name" type="text" bind:value={name} aria-invalid={!!errors.name} aria-describedby={errors.name ? "err-name-de err-name-en" : undefined} style={errors.name ? "border-color:var(--accent)" : ""} />
              {#if errors.name}
                <p id="err-name-de" role="alert" lang="de" style="color:var(--accent);font-size:0.8rem;margin-top:4px">{messages.name.de}</p>
                <p id="err-name-en" role="alert" lang="en" style="color:var(--accent);font-size:0.8rem;margin-top:4px">{messages.name.en}</p>
              {/if}
            </div>
            <div class="form-group">
              <label lang="de" for="email">E-Mail *</label>
              <label lang="en" for="email">Email *</label>
              <input id="email" type="email" bind:value={emailField} aria-invalid={!!errors.email} aria-describedby={errors.email ? "err-email-de err-email-en" : undefined} style={errors.email ? "border-color:var(--accent)" : ""} />
              {#if errors.email}
                <p id="err-email-de" role="alert" lang="de" style="color:var(--accent);font-size:0.8rem;margin-top:4px">{messages.email.de}</p>
                <p id="err-email-en" role="alert" lang="en" style="color:var(--accent);font-size:0.8rem;margin-top:4px">{messages.email.en}</p>
              {/if}
            </div>
            <div class="form-group">
              <label lang="de" for="phone">Telefon</label>
              <label lang="en" for="phone">Phone</label>
              <input id="phone" type="tel" placeholder="+43" autocomplete="tel" bind:value={phoneField} />
            </div>
            <div class="form-group">
              <label lang="de" for="role">Rolle / Funktion</label>
              <label lang="en" for="role">Role / function</label>
              <input id="role" type="text" autocomplete="organization-title" bind:value={role} />
            </div>
            <div class="form-group">
              <label lang="de" for="website">Website</label>
              <label lang="en" for="website">Website</label>
              <input id="website" type="url" inputmode="url" autocomplete="url" bind:value={website} />
            </div>
            <div class="form-group">
              <label lang="de" for="subject">Betreff</label>
              <label lang="en" for="subject">Subject</label>
              <div class="select-wrap">
                <select id="subject" bind:value={subject}>
                  <option value="" lang="de">— Bitte wählen —</option>
                  <option value="" lang="en">— Please select —</option>
                  <option value="webdesign">Webdesign & UX/UI</option>
                  <option value="development">Webdevelopment</option>
                  <option value="jamstack">JamStack</option>
                  <option value="seo">SEO / SEA</option>
                  <option value="ecommerce">Ecommerce</option>
                  <option value="webmanagement">Web Management</option>
                  <option value="ai">Frontier AI Development</option>
                  <option value="other" lang="de">Sonstiges</option>
                  <option value="other" lang="en">Other</option>
                </select>
                <svg class="select-chevron" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="m6 9 6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
            </div>
            <div class="form-group">
              <label lang="de" for="message">Nachricht *</label>
              <label lang="en" for="message">Message *</label>
              <textarea id="message" bind:value={message} aria-invalid={!!errors.message} aria-describedby={errors.message ? "err-message-de err-message-en" : undefined} style={errors.message ? "border-color:var(--accent)" : ""}></textarea>
              {#if errors.message}
                <p id="err-message-de" role="alert" lang="de" style="color:var(--accent);font-size:0.8rem;margin-top:4px">{messages.message.de}</p>
                <p id="err-message-en" role="alert" lang="en" style="color:var(--accent);font-size:0.8rem;margin-top:4px">{messages.message.en}</p>
              {/if}
            </div>
            <div class="form-group">
              <p style="font-size:0.8rem;color:var(--muted)">
                <span lang="de">Hinweis: Wie wir Ihre Daten verarbeiten, erfahren Sie in unserer <a href="/datenschutz">Datenschutzerklärung</a>.</span>
                <span lang="en">Note: Learn how we handle your data in our <a href="/datenschutz">privacy policy</a>.</span>
              </p>
            </div>

            <div class="hp-field" aria-hidden="true">
              <label for="company">Company</label>
              <input id="company" type="text" tabindex="-1" autocomplete="off" bind:value={company} />
            </div>
            {#if submitError}
              <div bind:this={errorEl}>
                <p role="alert" lang="de" style="color:var(--accent);font-size:0.85rem;margin-bottom:12px">Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.</p>
                <p role="alert" lang="en" style="color:var(--accent);font-size:0.85rem;margin-bottom:12px">Something went wrong. Please try again.</p>
              </div>
            {/if}
            <button type="submit" class="btn btn-primary" lang="de" disabled={submitting}>Nachricht senden</button>
            <button type="submit" class="btn btn-primary" lang="en" disabled={submitting}>Send message</button>
          </form>
        {/if}
      </div>

      <div style="display:flex;flex-direction:column;gap:var(--gap)">
        <div class="card">
          <h3 lang="de">30 Minuten. Kostenlos. Unverbindlich.</h3>
          <h3 lang="en">30 minutes. Free. No obligation.</h3>
          <p>
            <span lang="de">Lernen Sie uns kennen — ganz ohne Druck.</span>
            <span lang="en">Get to know us — no pressure.</span>
          </p>
          {#if bookingUrl && bookingUrl !== "#"}
            <a href={bookingUrl} class="btn btn-primary mt-1">
              <span lang="de">Jetzt Gespräch buchen</span>
              <span lang="en">Book a call now</span>
            </a>
          {/if}
        </div>
        <div class="card">
          <h4 lang="de">Kontakt</h4>
          <h4 lang="en">Contact</h4>
          <p style="margin-top:8px"><a href="mailto:{email}">{email}</a></p>
          <p><a href="tel:{phone.replace(/[^\d+]/g, '')}">{phone}</a></p>
          {#if linkedin !== "#"}
            <p style="margin-top:4px"><a href={linkedin}>LinkedIn</a></p>
          {/if}
          <p style="font-size:0.85rem;color:var(--muted);margin-top:8px" lang="de">Wir melden uns innerhalb von 24 Stunden.</p>
          <p style="font-size:0.85rem;color:var(--muted);margin-top:8px" lang="en">We'll respond within 24 hours.</p>
        </div>
      </div>
    </div>
  </div>
</section>
